// src/pages/Profile/Profile.jsx
import React, { useState, useEffect } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Edit,
  Save,
  X,
  Award,
  BookOpen,
  Briefcase,
  Link2,
  Brain,
  Settings,
  ClipboardCheck,
  Github,
  Linkedin,
  Globe,
  Code,
  Target,
  Calendar,
  Star,
  ChevronDown,
  ChevronRight,
  Award as AwardIcon,
  FileText,
  Image,
  Download,
  Trash2,
  Upload,
  ExternalLink,
} from "lucide-react";
import useAuthStore from "../../store/authStore";
import useUserStore from "../../store/userStore";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import toast from "react-hot-toast";
import SocialLinksInput from "../../components/Profile/SocialLinksInput";
import SkillConfidenceSection from "../../components/Profile/SkillConfidenceSection";
import AssessmentModal from "../../components/Assessment/AssessmentModal";
import api from "../../services/api";

const card = "bg-white rounded-lg shadow-sm border border-gray-100";
const cardHeader = "px-5 py-4 border-b border-gray-100";
const cardContent = "p-5";

const Profile = () => {
  const { user, token} = useAuthStore();
  const { profile, fetchProfile, updateProfile } = useUserStore();
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);

  // Optimized collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    projects: false, // Achievements & Resume
    socialLinks: false,
    learningPrefs: false,
    skillConfidence: false,
  });

  // Load profile data
  useEffect(() => {
    if (user) fetchProfile();
  }, [user, fetchProfile]);

  // Initialize form when profile/user data arrives
  useEffect(() => {
    if (profile || user) {
      const userData = profile?.user || {};
      const extended = profile?.extended || {};
      const behavior = profile?.behavior || {};
      const insights = profile?.insights || {};

      setFormData({
        // SQL fields from user object
        img_url: userData?.img_url || user?.img_url || "",
        first_name: userData?.first_name || user?.first_name || "",
        last_name: userData?.last_name || user?.last_name || "",
        email: userData?.email || user?.email || "",

        // Extended MongoDB fields
        phone: userData?.phone || user?.phone || extended?.phone || "",
        location:
          userData?.location || user?.location || extended?.location || "",
        bio: extended?.bio || "",

        // Achievements & Resume (file uploads)
        achievements: extended?.achievements || [],

        user_type: userData?.user_type || "learner",
        organisation: extended?.organisation || "",
        certifications: extended?.certifications || [],
        managed_courses: extended?.managed_courses || [],
        nsqf_level: extended?.nsqf_level || null,

        // Extended profile fields
        social_links: extended?.social_links || {},
        skill_confidence: extended?.skill_confidence || {},
        highest_qualification: extended?.highest_qualification || "",
        sector_preference: extended?.sector_preference || "",
        goal_type: extended?.goal_type || "",
        career_target: extended?.career_target || "",
        expected_salary: extended?.expected_salary || "",
        preferred_training_type: extended?.preferred_training_type || "",
        preferred_learning_style: extended?.preferred_learning_style || "",
        availability_hours_per_week:
          extended?.availability_hours_per_week || "",
        preferred_language: extended?.preferred_language || "English",
        budget_range: extended?.budget_range || "",
      });
    }
  }, [profile, user]);

  // Debug logging
  useEffect(() => {
    console.log("🔍 Profile Debug:");
    console.log("Auth user:", user);
    console.log("Profile data:", profile);
    console.log("Profile user:", profile?.user);
    console.log("Profile extended:", profile?.extended);
  }, [user, profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSocialLinksChange = (socialLinks) => {
    setFormData((s) => ({ ...s, social_links: socialLinks }));
  };

  const handleSkillConfidenceChange = (skillConfidence) => {
    setFormData((s) => ({ ...s, skill_confidence: skillConfidence }));
  };

  const handleAchievementsChange = (achievements) => {
    setFormData((s) => ({ ...s, achievements: achievements }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // File upload handlers
  const handleFileUpload = async (file, description = "") => {
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("description", description || file.name);

    try {
      const response = await api.post(
        "/users/upload-achievement",
        formDataUpload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        await fetchProfile(); // Refresh to show new file
        toast.success("File uploaded successfully!");
        return response.data.achievement;
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.error || "Failed to upload file");
      throw error;
    }
  };

  // Use a proper download link:
  const handleDownload = async (achievementId, filename) => {
    try {
      // ✅ Use the configured API service (handles base URL & auth automatically)
      const response = await api.get(`/users/download-achievement/${achievementId}`, {
        responseType: 'blob',  // ✅ Download as blob
      });
      
      // ✅ Create blob URL for download
      const url = window.URL.createObjectURL(response.data);
      
      // ✅ Create download link
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || 'download';
      link.style.display = "none";
      
      // ✅ Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // ✅ Clean up blob URL
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleFileDelete = async (achievementId) => {
    try {
      await api.delete(`/users/delete-achievement/${achievementId}`);
      await fetchProfile();
      toast.success("File deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete file");
    }
  };

  const handleSave = async () => {
    // Basic validation
    if (!formData.first_name?.trim()) {
      toast.error("First name is required");
      return;
    }

    // Build payload - separate SQL fields from MongoDB extended fields
    const sqlFields = ["first_name", "last_name", "email", "img_url"];
    const extendedFields = [
      "phone",
      "location",
      "bio",
      "achievements", // File uploads
      "social_links",
      "skill_confidence",
      "highest_qualification",
      "sector_preference",
      "goal_type",
      "career_target",
      "expected_salary",
      "preferred_training_type",
      "preferred_learning_style",
      "availability_hours_per_week",
      "preferred_language",
      "budget_range",
    ];

    const payload = {};

    // Add SQL fields
    sqlFields.forEach((k) => {
      if (formData[k] !== undefined) {
        payload[k] = formData[k] ?? "";
      }
    });

    // Add extended fields directly (no JSON.stringify needed)
    extendedFields.forEach((k) => {
      if (formData[k] !== undefined) {
        payload[k] = formData[k]; // Objects will be sent as-is
      }
    });

    // Save profile
    const result = await updateProfile(payload);

    if (result?.success) {
      toast.success("Profile updated successfully!");
      await fetchProfile(); // Ensure fresh data
        setIsEditing(false);
      } else {
      toast.error(result?.error || "Failed to update profile");
    }
  };

  const handleCancel = () => {
    // Reset to current profile data
    const userData = profile?.user || {};
    const extended = profile?.extended || {};

    setFormData({
      img_url: userData?.img_url || user?.img_url || "",
      first_name: userData?.first_name || user?.first_name || "",
      last_name: userData?.last_name || user?.last_name || "",
      email: userData?.email || user?.email || "",
      phone: userData?.phone || user?.phone || extended?.phone || "",
      location:
        userData?.location || user?.location || extended?.location || "",
      bio: extended?.bio || "",
      achievements: extended?.achievements || [],
      user_type: userData?.user_type || "learner",
      organisation: extended?.organisation || "",
      certifications: extended?.certifications || [],
      managed_courses: extended?.managed_courses || [],
      nsqf_level: extended?.nsqf_level || null,
      social_links: extended?.social_links || {},
      skill_confidence: extended?.skill_confidence || {},
      highest_qualification: extended?.highest_qualification || "",
      sector_preference: extended?.sector_preference || "",
      goal_type: extended?.goal_type || "",
      career_target: extended?.career_target || "",
      expected_salary: extended?.expected_salary || "",
      preferred_training_type: extended?.preferred_training_type || "",
      preferred_learning_style: extended?.preferred_learning_style || "",
      availability_hours_per_week: extended?.availability_hours_per_week || "",
      preferred_language: extended?.preferred_language || "English",
      budget_range: extended?.budget_range || "",
    });
    setIsEditing(false);
  };

  const getUserInitials = () => {
    const source = profile?.user || user || {};
    const { first_name = "", last_name = "" } = source;
    return (
      `${first_name?.[0] || ""}${last_name?.[0] || ""}`.toUpperCase() || "U"
    );
  };

  const getUserDisplayName = () => {
    const source = profile?.user || user || {};
    const name = `${source.first_name || ""} ${source.last_name || ""}`.trim();
    return name || source.email || "User";
  };

  const role = (
    formData.user_type ||
    profile?.user?.user_type ||
    "learner"
  ).toLowerCase();

  // Role-specific panels
  const LearnerPanel = () => (
    <div className={`${card}`}>
      <div className={cardHeader}>
        <h3 className="text-lg font-semibold">Learner Overview</h3>
        <p className="text-sm text-gray-500">
          Personalized learning & progress
        </p>
      </div>
      <div className={cardContent}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-500">
              Recommended Next Course
            </label>
            <div className="mt-1 text-sm text-gray-700">
              {profile?.extended?.recommended_course ||
                "Generate roadmap to see"}
          </div>
          </div>

          {/* Social Links Preview */}
          {formData.social_links &&
            Object.keys(formData.social_links).some(
              (key) => formData.social_links[key]
            ) && (
          <div>
                <label className="text-xs text-gray-500">
                  Connected Profiles
                </label>
                <div className="mt-1 text-sm text-gray-700">
                  {
                    Object.keys(formData.social_links).filter(
                      (key) => key && formData.social_links[key]
                    ).length
                  }{" "}
                  profiles linked
          </div>
              </div>
            )}

          {/* Achievements Preview */}
          {formData.achievements && formData.achievements.length > 0 && (
          <div>
              <label className="text-xs text-gray-500">Achievement Files</label>
              <div className="mt-1 text-sm text-gray-700">
                {formData.achievements.length} files uploaded
          </div>
        </div>
          )}
        </div>

        {/* Assessment Button */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={() => setShowAssessment(true)}
            className="w-full btn btn-primary"
          >
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Take Skill Assessment
          </button>
        </div>
      </div>
    </div>
  );

  const TrainerPanel = () => (
    <div className={`${card}`}>
      <div className={cardHeader}>
        <h3 className="text-lg font-semibold">Trainer Dashboard</h3>
        <p className="text-sm text-gray-500">
          Courses & certifications you manage
        </p>
      </div>
      <div className={cardContent}>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500">Organization</label>
            <div className="mt-1 text-sm text-gray-700">
              {formData.organisation ||
                profile?.extended?.organisation ||
                "Not provided"}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500">Managed Courses</label>
            <ul className="mt-2 space-y-1">
              {(profile?.extended?.managed_courses || []).length > 0 ? (
                (profile.extended.managed_courses || []).map((c, i) => (
                  <li key={i} className="text-sm text-gray-700">
                    • {c.title || c.name || c}
                  </li>
                ))
              ) : (
                <li className="text-sm text-gray-500">No courses found</li>
              )}
            </ul>
          </div>

          <div>
            <label className="text-xs text-gray-500">Certifications</label>
            <div className="mt-1 text-sm text-gray-700">
              {(profile?.extended?.certifications || []).length} certificates
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500">Experience Level</label>
            <div className="mt-1 text-sm text-gray-700">
              {profile?.extended?.experience_years || "Not specified"} years
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500">Specializations</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {(profile?.extended?.specializations || []).map((spec, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {spec}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const PolicymakerPanel = () => (
    <div className={`${card}`}>
      <div className={cardHeader}>
        <h3 className="text-lg font-semibold">Policymaker / Admin View</h3>
        <p className="text-sm text-gray-500">
          High-level metrics & NDVP/NCVET mappings
        </p>
      </div>
      <div className={cardContent}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500">Active Programs</div>
            <div className="text-lg font-medium mt-1">
              {profile?.extended?.admin_stats?.active_programs || 0}
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500">Learners Enrolled</div>
            <div className="text-lg font-medium mt-1">
              {profile?.extended?.admin_stats?.learners || 0}
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500">NSQF Alignments</div>
            <div className="text-lg font-medium mt-1">
              {profile?.extended?.admin_stats?.nsqf_mapped || 0}
            </div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          Use the policymaker panel to view aggregated metrics and export
          reports (feature placeholder).
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header card */}
      <div className={`${card} p-6 flex items-center justify-between`}>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center text-2xl font-semibold text-gray-700 overflow-hidden shadow">
            {formData.img_url || profile?.user?.img_url || user?.img_url ? (
              <img
                src={
                  formData.img_url ||
                  profile?.user?.img_url ||
                  user?.img_url ||
                  ""
                }
                alt={getUserDisplayName()}
                className="h-20 w-20 object-cover rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              getUserInitials()
            )}
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{getUserDisplayName()}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
              <div className="inline-flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <span>{formData.email || profile?.user?.email || "—"}</span>
              </div>
              <div className="inline-flex items-center gap-2 pl-4 border-l border-gray-100">
                <BookOpen className="h-4 w-4 text-gray-400" />
                <span className="capitalize">{role}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsEditing((s) => !s)}
            className="px-3 py-2 rounded-md border border-gray-200 text-sm bg-white hover:shadow transition"
          >
            {isEditing ? (
              <div className="flex items-center gap-2">
                <X className="h-4 w-4" /> Cancel
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Edit className="h-4 w-4" /> Edit Profile
              </div>
            )}
          </button>

          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-3 py-2 rounded-md bg-primary-600 text-white text-sm hover:shadow"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <div className="flex items-center gap-2">
                  <Save className="h-4 w-4" /> Save
                </div>
              )}
            </button>
          )}
        </div>
      </div>

      {/* OPTIMIZED LAYOUT: Better side-by-side collapsible sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column - Personal & Social */}
        <div className="space-y-6">
          {/* Personal Information - Always Expanded */}
          <div className={card}>
            <div className={cardHeader}>
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Personal Information</h3>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {isEditing
                  ? "Update your personal details"
                  : "Your basic information"}
              </p>
            </div>

            <div className={cardContent}>
              {isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">First name</label>
                  <input
                    name="first_name"
                    value={formData.first_name || ""}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Last name</label>
                  <input
                    name="last_name"
                    value={formData.last_name || ""}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <div className="mt-1 relative">
                      <div className="absolute left-3 top-3 pointer-events-none text-gray-400">
                        <Mail className="h-4 w-4" />
                      </div>
                    <input
                      name="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="pl-10 mt-0 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">Phone</label>
                  <div className="mt-1 relative">
                      <div className="absolute left-3 top-3 pointer-events-none text-gray-400">
                        <Phone className="h-4 w-4" />
                      </div>
                    <input
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="pl-10 mt-0 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">Location</label>
                  <div className="mt-1 relative">
                      <div className="absolute left-3 top-3 pointer-events-none text-gray-400">
                        <MapPin className="h-4 w-4" />
                      </div>
                    <input
                      name="location"
                      value={formData.location || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="pl-10 mt-0 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio || ""}
                    onChange={handleChange}
                    disabled={!isEditing}
                    rows={4}
                    className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                  />
                </div>
              </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">First Name</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {formData.first_name || "Not provided"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Last Name</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {formData.last_name || "Not provided"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Email</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {formData.email || "Not provided"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Phone</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {formData.phone || "Not provided"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Location</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {formData.location || "Not provided"}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-500">Bio</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {formData.bio || "No bio provided"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Social Links - Dynamic Display */}
          {role === "learner" && (
          <div className={card}>
              <div
                className={`${cardHeader} cursor-pointer hover:bg-gray-50 transition`}
                onClick={() => toggleSection("socialLinks")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold">Social Links</h3>
                    {Object.values(formData.social_links || {}).some(
                      (link) => link
                    ) && (
                      <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                        {
                          Object.values(formData.social_links).filter(
                            (link) => link
                          ).length
                        }{" "}
                        linked
                      </span>
                    )}
            </div>
                  {expandedSections.socialLinks ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditing
                    ? "Connect your profiles to help AI understand your background"
                    : "Your connected profiles"}
                </p>
              </div>

              {expandedSections.socialLinks && (
            <div className={cardContent}>
                  {isEditing ? (
                    <SocialLinksInput
                      socialLinks={formData.social_links || {}}
                      onChange={handleSocialLinksChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    // DYNAMIC VIEW MODE: Show ALL social links that have values
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(() => {
                        // Define social platform configs
                        const platforms = {
                          github: {
                            icon: Github,
                            color: "text-gray-700",
                            label: "GitHub",
                            desc: "View code repositories",
                          },
                          linkedin: {
                            icon: Linkedin,
                            color: "text-blue-600",
                            label: "LinkedIn",
                            desc: "Professional network",
                          },
                          portfolio: {
                            icon: Globe,
                            color: "text-green-600",
                            label: "Portfolio",
                            desc: "View projects",
                          },
                          leetcode: {
                            icon: Code,
                            color: "text-orange-600",
                            label: "LeetCode",
                            desc: "Coding problems",
                          },
                          behance: {
                            icon: Globe,
                            color: "text-pink-600",
                            label: "Behance",
                            desc: "Design portfolio",
                          },
                          dribbble: {
                            icon: Globe,
                            color: "text-purple-600",
                            label: "Dribbble",
                            desc: "Design work",
                          },
                          kaggle: {
                            icon: Code,
                            color: "text-blue-500",
                            label: "Kaggle",
                            desc: "Data science",
                          },
                          medium: {
                            icon: FileText,
                            color: "text-black",
                            label: "Medium",
                            desc: "Blog posts",
                          },
                          stackoverflow: {
                            icon: Code,
                            color: "text-orange-500",
                            label: "Stack Overflow",
                            desc: "Q&A platform",
                          },
                        };

                        // Filter and map social links that have values
                        const activeLinks = Object.entries(
                          formData.social_links || {}
                        )
                          .filter(([key, value]) => value && value.trim())
                          .map(([key, value]) => {
                            const platform = platforms[key];
                            if (!platform) return null;

                            const IconComponent = platform.icon;
                            return (
                              <a
                                key={key}
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                              >
                                <IconComponent
                                  className={`h-5 w-5 ${platform.color}`}
                                />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {platform.label}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {platform.desc}
                                  </div>
                                </div>
                              </a>
                            );
                          })
                          .filter(Boolean); // Remove null entries

                        return activeLinks.length > 0 ? (
                          activeLinks
                        ) : (
                          <div className="col-span-full text-sm text-gray-500">
                            No social links connected yet
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Achievements, Learning, Skills */}
        <div className="space-y-6">
          {/* Achievements & Resume - File Upload */}
          <div className={card}>
            <div
              className={`${cardHeader} cursor-pointer hover:bg-gray-50 transition`}
              onClick={() => toggleSection("projects")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AwardIcon className="h-5 w-5 text-primary-600" />
                  <h3 className="text-lg font-semibold">
                    Achievements & Resume
                  </h3>
                  {formData.achievements?.length > 0 && (
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      {formData.achievements.length} files
                    </span>
                  )}
                </div>
                {expandedSections.projects ? (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {isEditing
                  ? "Upload your resume, certificates, and achievement documents"
                  : "Your uploaded achievements and documents"}
              </p>
            </div>

            {expandedSections.projects && (
              <div className={cardContent}>
                {isEditing ? (
                  // EDIT MODE: File upload interface
                  <div className="space-y-4">
                    {/* Existing files list */}
                    {formData.achievements &&
                      formData.achievements.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-900">
                            Uploaded Files:
                          </h4>
                          {formData.achievements.map((achievement, index) => (
                            <div
                              key={achievement.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                {achievement.file_type === "pdf" ? (
                                  <FileText className="h-5 w-5 text-red-500" />
                                ) : ["png", "jpg", "jpeg", "gif"].includes(
                                    achievement.file_type
                                  ) ? (
                                  <Image className="h-5 w-5 text-green-500" />
                                ) : (
                                  <FileText className="h-5 w-5 text-gray-500" />
                                )}
                                <div>
                                  <div className="font-medium text-sm">
                                    {achievement.original_name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(achievement.file_size / 1024).toFixed(1)}{" "}
                                    KB
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    handleDownload(
                                      achievement.id,
                                      achievement.original_name
                                    )
                                  }
                                  className="p-1 text-gray-500 hover:text-primary-600"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleFileDelete(achievement.id)
                                  }
                                  className="p-1 text-gray-500 hover:text-red-600"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                  ))}
                </div>
                      )}

                    {/* File upload area */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <div className="text-lg font-medium text-gray-900 mb-2">
                          Upload Achievement Files
              </div>
                        <p className="text-gray-500 mb-4">
                          Upload PDFs, images, certificates, or resume files
                          (max 10MB each)
                        </p>

                        <input
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.gif"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);

                            for (const file of files) {
                              if (file.size > 10 * 1024 * 1024) {
                                // 10MB limit
                                toast.error(
                                  `${file.name} is too large (max 10MB)`
                                );
                                continue;
                              }

                              // const category = file.type.includes('pdf') ? 'resume' : 'certificate';
                              try {
                                await handleFileUpload(file, file.name);
                              } catch (error) {
                                // Error already handled in handleFileUpload
                              }
                            }

                            // Reset input
                            e.target.value = "";
                          }}
                          className="hidden"
                          id="achievement-upload"
                        />

                        <label
                          htmlFor="achievement-upload"
                          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer transition"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Choose Files
                        </label>
                      </div>
                    </div>
                  </div>
                ) : (
                  // VIEW MODE: Display uploaded files
                  <div className="space-y-4">
                    {formData.achievements &&
                    formData.achievements.length > 0 ? (
                      formData.achievements.map((achievement, index) => (
                        <div
                          key={achievement.id}
                          className="p-4 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {achievement.file_type === "pdf" ? (
                                <FileText className="h-6 w-6 text-red-500" />
                              ) : ["png", "jpg", "jpeg", "gif"].includes(
                                  achievement.file_type
                                ) ? (
                                <Image className="h-6 w-6 text-green-500" />
                              ) : (
                                <FileText className="h-6 w-6 text-gray-500" />
                              )}
              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {achievement.original_name}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-500">
                                    {(achievement.file_size / 1024).toFixed(1)}{" "}
                                    KB
                                  </span>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() =>
                                handleDownload(
                                  achievement.id,
                                  achievement.original_name
                                )
                              }
                              className="p-2 text-primary-600 hover:text-primary-800 rounded-lg hover:bg-primary-50 transition"
                              title="Download"
                            >
                              <Download className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <AwardIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No achievement files uploaded yet</p>
                        <p className="text-sm">
                          Click edit to upload your resume, certificates, and
                          achievements
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Learning Preferences - Enhanced View Mode */}
          {role === "learner" && (
            <div className={card}>
              <div
                className={`${cardHeader} cursor-pointer hover:bg-gray-50 transition`}
                onClick={() => toggleSection("learningPrefs")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold">
                      Learning Preferences
                    </h3>
                    {(formData.preferred_training_type ||
                      formData.preferred_learning_style ||
                      formData.career_target) && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        Set
                      </span>
                    )}
                  </div>
                  {expandedSections.learningPrefs ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditing
                    ? "Tell us how you prefer to learn"
                    : "Your learning preferences"}
                </p>
              </div>

              {expandedSections.learningPrefs && (
                <div className={cardContent}>
                  {isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">
                          Training Type
                        </label>
                        <select
                          name="preferred_training_type"
                          value={formData.preferred_training_type || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        >
                          <option value="">Select...</option>
                          <option value="online">Online</option>
                          <option value="offline">Offline</option>
                          <option value="hybrid">Hybrid</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Learning Style
                        </label>
                        <select
                          name="preferred_learning_style"
                          value={formData.preferred_learning_style || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        >
                          <option value="">Select...</option>
                          <option value="visual">Visual</option>
                          <option value="auditory">Auditory</option>
                          <option value="kinesthetic">Kinesthetic</option>
                          <option value="reading">Reading/Writing</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Availability (hours/week)
                        </label>
                        <input
                          type="number"
                          name="availability_hours_per_week"
                          value={formData.availability_hours_per_week || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          min="0"
                          max="168"
                          placeholder="e.g., 10"
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Preferred Language
                        </label>
                        <select
                          name="preferred_language"
                          value={formData.preferred_language || "English"}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        >
                          <option value="English">English</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Budget Range
                        </label>
                        <select
                          name="budget_range"
                          value={formData.budget_range || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        >
                          <option value="">Select...</option>
                          <option value="free">Free</option>
                          <option value="under_5k">Under ₹5,000</option>
                          <option value="5k_10k">₹5,000 - ₹10,000</option>
                          <option value="10k_25k">₹10,000 - ₹25,000</option>
                          <option value="25k_plus">₹25,000+</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Highest Qualification
                        </label>
                        <input
                          type="text"
                          name="highest_qualification"
                          value={formData.highest_qualification || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          placeholder="e.g., B.Tech, M.Sc, Diploma"
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Sector Preference
                        </label>
                        <input
                          type="text"
                          name="sector_preference"
                          value={formData.sector_preference || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          placeholder="e.g., IT, Healthcare, Finance"
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Goal Type
                        </label>
                        <select
                          name="goal_type"
                          value={formData.goal_type || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        >
                          <option value="">Select...</option>
                          <option value="job">Get a Job</option>
                          <option value="upskill">Upskill</option>
                          <option value="career_switch">Career Switch</option>
                          <option value="promotion">Get Promotion</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Career Target
                        </label>
                        <input
                          type="text"
                          name="career_target"
                          value={formData.career_target || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          placeholder="e.g., Data Scientist, Full Stack Developer"
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">
                          Expected Salary
                        </label>
                        <input
                          type="text"
                          name="expected_salary"
                          value={formData.expected_salary || ""}
                          onChange={handleChange}
                          disabled={!isEditing}
                          placeholder="e.g., ₹5-10 LPA"
                          className="mt-1 block w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring"
                        />
                      </div>
                    </div>
                  ) : (
                    // ENHANCED VIEW MODE: Show ALL data, not just if it exists
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">
                          Training Type
                        </label>
                        <div className="mt-1 text-sm text-gray-900 capitalize">
                          {formData.preferred_training_type || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Learning Style
                        </label>
                        <div className="mt-1 text-sm text-gray-900 capitalize">
                          {formData.preferred_learning_style || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Weekly Availability
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.availability_hours_per_week
                            ? `${formData.availability_hours_per_week} hours`
                            : "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Preferred Language
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.preferred_language || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Budget Range
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.budget_range || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Highest Qualification
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.highest_qualification || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Sector Preference
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.sector_preference || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Goal Type
                        </label>
                        <div className="mt-1 text-sm text-gray-900 capitalize">
                          {formData.goal_type || "Not set"}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs text-gray-500">
                          Career Target
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.career_target || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Expected Salary
                        </label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formData.expected_salary || "Not set"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Skill Confidence - Collapsible (Learners only) */}
          {role === "learner" && (
            <div className={card}>
              <div
                className={`${cardHeader} cursor-pointer hover:bg-gray-50 transition`}
                onClick={() => toggleSection("skillConfidence")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold">Skill Confidence</h3>
                    {Object.keys(formData.skill_confidence || {}).length >
                      0 && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                        {Object.keys(formData.skill_confidence).length} rated
                      </span>
                    )}
                  </div>
                  {expandedSections.skillConfidence ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditing
                    ? "Rate your confidence level for each skill (0-5 scale)"
                    : "Your skill confidence ratings"}
                </p>
              </div>

              {expandedSections.skillConfidence && (
                <div className={cardContent}>
                  {isEditing ? (
                    <SkillConfidenceSection
                      skillConfidence={formData.skill_confidence || {}}
                      onChange={handleSkillConfidenceChange}
                      disabled={!isEditing}
                    />
                  ) : (
                    <div className="space-y-3">
                      {Object.keys(formData.skill_confidence || {}).length >
                      0 ? (
                        Object.entries(formData.skill_confidence).map(
                          ([skill, level]) => (
                            <div
                              key={skill}
                              className="flex items-center justify-between"
                            >
                              <span className="text-sm font-medium text-gray-900">
                                {skill}
                              </span>
                              <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      className={`h-4 w-4 ${
                                        star <= level
                                          ? "text-yellow-400 fill-current"
                                          : "text-gray-300"
                                      }`}
                                    />
                  ))}
                </div>
                                <span className="text-xs text-gray-500">
                                  ({level}/5)
                                </span>
              </div>
            </div>
                          )
                        )
                      ) : (
                        <div className="text-sm text-gray-500">
                          No skill confidence ratings yet
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
        </div>

      {/* Account Status Sidebar */}
        <div className="space-y-6">
        <div className={`${card}`}>
            <div className={cardHeader}>
              <h3 className="text-lg font-semibold">Account Status</h3>
            </div>
            <div className={cardContent}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">User Type</div>
                  <div className="text-sm font-medium capitalize flex items-center gap-2">
                  {role === "trainer" ? (
                    <Award className="h-4 w-4" />
                  ) : role === "policymaker" ? (
                    <Briefcase className="h-4 w-4" />
                  ) : (
                    <BookOpen className="h-4 w-4" />
                  )}
                    <span>{role}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">Member Since</div>
                <div className="text-sm font-medium">
                  {profile?.user?.created_at
                    ? new Date(profile.user.created_at).toLocaleDateString()
                    : "—"}
                </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">Status</div>
                  <div className="text-sm font-medium text-green-600">Active</div>
                </div>
              </div>
            </div>
          </div>

          {/* Role panel */}
          {role === "learner" && <LearnerPanel />}
          {role === "trainer" && <TrainerPanel />}
          {role === "policymaker" && <PolicymakerPanel />}
      </div>

      {/* Save/Cancel actions at bottom (mobile-friendly) */}
      {isEditing && (
        <div className="flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded border border-gray-200 bg-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 rounded bg-primary-600 text-white"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : "Save changes"}
          </button>
        </div>
      )}

      {/* Assessment Modal */}
      <AssessmentModal
        isOpen={showAssessment}
        onClose={() => setShowAssessment(false)}
        onComplete={(results) => {
          console.log("Assessment completed:", results);
          fetchProfile(); // Refresh profile data
        }}
      />
    </div>
  );
};

export default Profile;
